// src/components/Equipe.js
// Onglet "Équipe" : qui a accès à la tombola active, invitations, rôles.
// (Anciennement dans "Mon profil")
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTombola } from "../contexts/TombolaContext";
import { useProfil } from "../contexts/ProfilContext";
import { creerInvitations } from "../services/brevo";
import { db } from "../services/firebase";
import { doc, updateDoc, deleteDoc, collection, onSnapshot } from "firebase/firestore";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Equipe() {
  const { currentUser, orgId } = useAuth();
  const { activeTombola, activeTombolaId, membres } = useTombola();
  const { monPseudo } = useProfil();

  const [msg, setMsg] = useState(null);
  const [role, setRole] = useState("benevole");
  const [emails, setEmails] = useState([""]);
  const [envoi, setEnvoi] = useState(false);
  const [invitations, setInvitations] = useState([]);

  useEffect(() => {
    if (!orgId || !activeTombolaId) return;
    const unsub = onSnapshot(
      collection(db, "organisations", orgId, "tombolas", activeTombolaId, "invitations"),
      (snap) => setInvitations(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setInvitations([])
    );
    return unsub;
  }, [orgId, activeTombolaId]);

  function showMsg(text, type = "success") {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  }

  const emailsValides = emails.map((e) => e.trim().toLowerCase()).filter((e) => EMAIL_REGEX.test(e));

  async function envoyer(e) {
    e.preventDefault();
    if (emailsValides.length === 0) { showMsg("Ajoutez au moins un email valide.", "error"); return; }
    setEnvoi(true);
    try {
      const { envoyees = 0, echecs = [] } = await creerInvitations({
        orgId, tombolaId: activeTombolaId, emails: emailsValides, role,
      });
      if (echecs.length > 0) showMsg(`${envoyees} envoyée(s). Échec pour : ${echecs.join(", ")}`, "error");
      else showMsg(`✅ ${envoyees} invitation${envoyees > 1 ? "s envoyées" : " envoyée"} !`);
      setEmails([""]);
    } catch (err) {
      showMsg(err.message || "Erreur lors de l'envoi des invitations.", "error");
    }
    setEnvoi(false);
  }

  async function changerRole(userId, nouveauRole) {
    try {
      await updateDoc(doc(db, "organisations", orgId, "tombolas", activeTombolaId, "membres", userId), { role: nouveauRole });
      showMsg("✅ Rôle modifié.");
    } catch { showMsg("Erreur lors du changement de rôle.", "error"); }
  }

  async function retirer(m) {
    if (!window.confirm(`Retirer ${m.pseudo || m.email} de cette tombola ?`)) return;
    try {
      await deleteDoc(doc(db, "organisations", orgId, "tombolas", activeTombolaId, "membres", m.userId || m.id));
      showMsg("✅ Accès retiré.");
    } catch { showMsg("Erreur.", "error"); }
  }

  async function annulerInvitation(inv) {
    if (!window.confirm(`Annuler l'invitation de ${inv.email} ?`)) return;
    try {
      await deleteDoc(doc(db, "organisations", orgId, "tombolas", activeTombolaId, "invitations", inv.id));
    } catch { showMsg("Erreur.", "error"); }
  }

  function copierLien(inv) {
    navigator.clipboard.writeText(`${window.location.origin}/rejoindre/${orgId}/${activeTombolaId}/${inv.id}`);
    showMsg("📋 Lien copié : envoyez-le par SMS ou WhatsApp.");
  }

  const enAttente = invitations
    .filter((i) => i.status === "pending")
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  const membresTries = [...membres].sort((a, b) =>
    (a.role === "admin" ? 0 : 1) - (b.role === "admin" ? 0 : 1) ||
    String(a.pseudo || a.email).localeCompare(String(b.pseudo || b.email))
  );

  if (!activeTombola) return <p style={s.hint}>Créez d'abord une tombola.</p>;

  return (
    <div style={s.page}>
      <h2 style={s.title}>👥 Équipe</h2>
      <p style={s.intro}>
        Les personnes qui ont accès à <strong>{activeTombola.nom}</strong>.
        Les bénévoles vendent des billets ; les admins gèrent tout.
      </p>

      {msg && <div style={{ ...s.msg, ...(msg.type === "error" ? s.msgErr : s.msgOk) }}>{msg.text}</div>}

      {/* ── Membres ── */}
      <section style={s.section}>
        <h3 style={s.sectionTitle}>Membres ({membresTries.length})</h3>
        <div style={s.list}>
          {membresTries.map((m) => {
            const moi = (m.userId || m.id) === currentUser?.uid;
            const nom = moi ? monPseudo : (m.pseudo || (m.email || "").split("@")[0]);
            return (
              <div key={m.id} style={s.row}>
                <div style={s.avatar}>{nom.charAt(0).toUpperCase()}</div>
                <div style={s.rowMain}>
                  <div style={s.rowName}>{nom}{moi && <span style={s.vous}> (vous)</span>}</div>
                  <div style={s.rowSub}>{m.email}</div>
                </div>
                {moi ? (
                  <span style={m.role === "admin" ? s.pillAdmin : s.pillBenevole}>
                    {m.role === "admin" ? "👑 Admin" : "🤝 Bénévole"}
                  </span>
                ) : (
                  <div style={s.rowActions}>
                    <select value={m.role} onChange={(e) => changerRole(m.userId || m.id, e.target.value)} style={s.select}>
                      <option value="benevole">🤝 Bénévole</option>
                      <option value="admin">👑 Admin</option>
                    </select>
                    <button onClick={() => retirer(m)} style={s.btnIcon} title="Retirer l'accès">✕</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Inviter ── */}
      <section style={s.section}>
        <h3 style={s.sectionTitle}>Inviter des personnes</h3>
        <form onSubmit={envoyer} style={s.form}>
          <div style={s.roleChoice}>
            {[
              { id: "benevole", titre: "🤝 Bénévole", desc: "Vend des billets" },
              { id: "admin", titre: "👑 Admin", desc: "Gère lots, tirage, équipe" },
            ].map((r) => (
              <button key={r.id} type="button" onClick={() => setRole(r.id)}
                style={{ ...s.roleCard, ...(role === r.id ? s.roleCardActive : {}) }}>
                <span style={s.roleTitre}>{r.titre}</span>
                <span style={s.roleDesc}>{r.desc}</span>
              </button>
            ))}
          </div>

          {emails.map((email, idx) => (
            <div key={idx} style={s.emailRow}>
              <input type="email" placeholder="email@exemple.fr" value={email}
                onChange={(e) => setEmails((p) => p.map((x, i) => (i === idx ? e.target.value : x)))}
                style={s.input} />
              {emails.length > 1 && (
                <button type="button" onClick={() => setEmails((p) => p.filter((_, i) => i !== idx))}
                  style={s.btnIcon} title="Retirer">✕</button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setEmails((p) => [...p, ""])} style={s.btnGhost}>
            + Ajouter une adresse
          </button>
          <button type="submit" disabled={envoi || emailsValides.length === 0}
            style={{ ...s.btnPrimary, ...(envoi || emailsValides.length === 0 ? s.btnDisabled : {}) }}>
            {envoi ? "Envoi…" : `📧 Envoyer ${emailsValides.length > 1 ? `${emailsValides.length} invitations` : "l'invitation"}`}
          </button>
          <p style={s.hint}>Chaque personne reçoit un lien personnel, valable 14 jours.</p>
        </form>
      </section>

      {/* ── Invitations en attente ── */}
      {enAttente.length > 0 && (
        <section style={s.section}>
          <h3 style={s.sectionTitle}>En attente ({enAttente.length})</h3>
          <div style={s.list}>
            {enAttente.map((inv) => (
              <div key={inv.id} style={s.row}>
                <div style={{ ...s.avatar, ...s.avatarPending }}>✉️</div>
                <div style={s.rowMain}>
                  <div style={s.rowName}>{inv.email}</div>
                  <div style={s.rowSub}>
                    {inv.role === "admin" ? "👑 Admin" : "🤝 Bénévole"} · envoyée le{" "}
                    {inv.createdAt?.toDate?.().toLocaleDateString("fr-FR") || "—"}
                  </div>
                </div>
                <div style={s.rowActions}>
                  <button onClick={() => copierLien(inv)} style={s.btnSmall} title="Copier le lien (SMS, WhatsApp…)">📋 Lien</button>
                  <button onClick={() => annulerInvitation(inv)} style={s.btnIcon} title="Annuler l'invitation">✕</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const s = {
  page: { maxWidth: "560px", margin: "0 auto" },
  title: { fontSize: "1.25rem", fontWeight: "700", color: "#1a1a2e", margin: "0 0 0.35rem" },
  intro: { fontSize: "0.88rem", color: "#777", lineHeight: 1.6, margin: "0 0 1.25rem" },
  msg: { borderRadius: "10px", padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: "0.88rem" },
  msgOk: { background: "#e8f5e9", color: "#1b5e20" },
  msgErr: { background: "#ffebee", color: "#b71c1c" },
  section: { background: "#f7f8fc", borderRadius: "14px", padding: "1.1rem 1.25rem", marginBottom: "1rem", border: "1px solid #eee" },
  sectionTitle: { fontSize: "0.95rem", fontWeight: "700", color: "#333", margin: "0 0 0.85rem" },
  list: { display: "flex", flexDirection: "column", gap: "8px" },
  row: { display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #eee", borderRadius: "12px", padding: "10px 12px" },
  avatar: { width: "36px", height: "36px", borderRadius: "50%", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "700", fontSize: "0.95rem", flexShrink: 0 },
  avatarPending: { background: "#fff8e1", color: "#f57c00", fontSize: "0.9rem" },
  rowMain: { flex: 1, minWidth: 0 },
  rowName: { fontWeight: "600", color: "#1a1a2e", fontSize: "0.88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowSub: { fontSize: "0.74rem", color: "#999", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  vous: { color: "#999", fontWeight: "400" },
  rowActions: { display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 },
  pillAdmin: { background: "#eef0ff", color: "#4c5ee8", borderRadius: "20px", padding: "3px 10px", fontSize: "0.74rem", fontWeight: "700", flexShrink: 0 },
  pillBenevole: { background: "#e8f5e9", color: "#2e7d32", borderRadius: "20px", padding: "3px 10px", fontSize: "0.74rem", fontWeight: "700", flexShrink: 0 },
  select: { padding: "5px 8px", border: "1.5px solid #e0e0e0", borderRadius: "8px", fontSize: "0.8rem", background: "#fff", cursor: "pointer" },
  btnIcon: { width: "32px", height: "32px", flexShrink: 0, background: "#fff", border: "1.5px solid #ffcdd2", color: "#c62828", borderRadius: "8px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "700" },
  btnSmall: { background: "#eef0ff", border: "1px solid #c7d2ff", color: "#4c5ee8", borderRadius: "8px", padding: "6px 10px", cursor: "pointer", fontSize: "0.78rem", fontWeight: "600" },
  form: { display: "flex", flexDirection: "column", gap: "10px" },
  roleChoice: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" },
  roleCard: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px", padding: "10px 12px", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: "12px", cursor: "pointer", textAlign: "left" },
  roleCardActive: { border: "1.5px solid #667eea", background: "#eef0ff" },
  roleTitre: { fontWeight: "700", fontSize: "0.88rem", color: "#1a1a2e" },
  roleDesc: { fontSize: "0.74rem", color: "#888" },
  emailRow: { display: "flex", gap: "8px", alignItems: "center" },
  input: { flex: 1, minWidth: 0, padding: "0.65rem 0.9rem", border: "1.5px solid #e0e0e0", borderRadius: "10px", fontSize: "0.92rem", outline: "none", boxSizing: "border-box", background: "#fff" },
  btnGhost: { padding: "0.6rem", background: "#fff", border: "1.5px dashed #c7d2ff", color: "#667eea", borderRadius: "10px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "600" },
  btnPrimary: { padding: "0.75rem", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", cursor: "pointer", fontSize: "0.92rem" },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  hint: { fontSize: "0.78rem", color: "#999", margin: 0, textAlign: "center" },
};
